import { formatCents } from '../../components/SalesReport'
import { ProgressTone } from '../../components/SalesReport/ProgressBar'
import {
  AccessEventStatus,
  AccessMetrics,
  AccessReportGroup,
  AccessReportResponse,
} from '../../hooks/access-report'
import { deviceLabel } from './filters'

export type FlowMode = 'interval' | 'cumulative'
export type FlowGranularity = 1 | 5 | 15 | 60
export type HeatmapScale = 'row' | 'all'
export type DeviceStatus = 'Lendo' | 'Ocioso' | 'Sem leitura' | 'Encerrado' | '—'

export const FLOW_GRANULARITIES: FlowGranularity[] = [1, 5, 15, 60]
export const DEFAULT_GRANULARITY: FlowGranularity = 15
export const LIVE_PACE_MINUTES = 5
const MAX_FILLED_BUCKETS = 1500

const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/

export const minuteOf = (local: string): number => {
  const parts = LOCAL.exec(local)
  if (parts === null) return NaN
  const [, year, month, day, hour, minute] = parts.map(Number)
  return Date.UTC(year, month - 1, day, hour, minute) / 60000
}

const pad = (value: number): string => String(value).padStart(2, '0')

export const localOf = (minute: number): string => {
  const date = new Date(minute * 60000)
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(date.getUTCHours())}:${pad(
    date.getUTCMinutes()
  )}`
}

export const formatClock = (local: string | null | undefined): string => {
  const parts = local == null ? null : LOCAL.exec(local)
  return parts === null ? '—' : `${parts[4]}:${parts[5]}`
}

export const formatDayMonth = (local: string | null | undefined): string => {
  const parts = local == null ? null : LOCAL.exec(local)
  return parts === null ? '—' : `${parts[3]}/${parts[2]}`
}

export const formatDayTime = (local: string | null | undefined): string => {
  const parts = local == null ? null : LOCAL.exec(local)
  return parts === null ? '—' : `${parts[3]}/${parts[2]} ${parts[4]}:${parts[5]}`
}

export const formatCount = (value: number): string => value.toLocaleString('pt-BR')

export const formatRate = (value: number): string => value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })

export const formatAttendance = (value: number | null): string =>
  value === null ? '—' : `${(value * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`

export const attendanceTone = (value: number): ProgressTone => (value >= 0.8 ? 'positive' : value >= 0.5 ? 'warning' : 'danger')

export const optionalCents = (cents: number | null | undefined): string => (cents == null ? '—' : formatCents(cents))

export interface FlowPoint {
  start: string
  entries: number
}

export const bucketSeries = (series: Array<[string, number]>, granularity: FlowGranularity): FlowPoint[] => {
  const buckets = new Map<number, number>()
  for (const [local, entries] of series) {
    const minute = minuteOf(local)
    if (Number.isNaN(minute)) continue
    const bucket = Math.floor(minute / granularity) * granularity
    buckets.set(bucket, (buckets.get(bucket) ?? 0) + entries)
  }
  const keys = Array.from(buckets.keys()).sort((a, b) => a - b)
  if (keys.length === 0) return []
  const first = keys[0]
  const last = keys[keys.length - 1]
  const filled = (last - first) / granularity + 1 <= MAX_FILLED_BUCKETS
  const starts = filled
    ? Array.from({ length: (last - first) / granularity + 1 }, (_, index) => first + index * granularity)
    : keys
  return starts.map((start) => ({ start: localOf(start), entries: buckets.get(start) ?? 0 }))
}

export const cumulativeSeries = (points: FlowPoint[], issued: number | null): FlowPoint[] => {
  let running = 0
  return points.map((point) => {
    running += point.entries
    return { start: point.start, entries: issued !== null && issued > 0 ? (running / issued) * 100 : running }
  })
}

export const peakIndex = (points: FlowPoint[]): number =>
  points.reduce((best, point, index) => (point.entries > (points[best]?.entries ?? -1) ? index : best), -1)

export const hourlyBars = (series: Array<[string, number]>): FlowPoint[] =>
  bucketSeries(series, 60).filter((point) => point.entries > 0)

export interface HeatmapColumn {
  hour: string | null
  index: number
}

export const heatmapColumns = (hours: string[]): HeatmapColumn[] => {
  const columns: HeatmapColumn[] = []
  hours.forEach((hour, index) => {
    if (index > 0 && minuteOf(hour) - minuteOf(hours[index - 1]) > 60) columns.push({ hour: null, index: -1 })
    columns.push({ hour, index })
  })
  return columns
}

export const heatIntensity = (value: number, rowMax: number, globalMax: number, scale: HeatmapScale): number => {
  const max = scale === 'row' ? rowMax : globalMax
  return max > 0 ? value / max : 0
}

export const deviceStatus = (lastAt: string, status: AccessEventStatus, now: string): DeviceStatus => {
  if (status === 'ended') return 'Encerrado'
  if (status !== 'live') return '—'
  const idle = minuteOf(now) - minuteOf(lastAt)
  if (idle <= 5) return 'Lendo'
  if (idle <= 15) return 'Ocioso'
  return 'Sem leitura'
}

export const currentPace = (series: Array<[string, number]>, now: string): number => {
  const current = minuteOf(now)
  const recent = series.reduce((sum, [local, entries]) => {
    const age = current - minuteOf(local)
    return age >= 0 && age < LIVE_PACE_MINUTES ? sum + entries : sum
  }, 0)
  return recent / LIVE_PACE_MINUTES
}

export type ChipTone = 'good' | 'crit' | 'warn' | 'info'

export interface AttendanceChip {
  key: string
  tone: ChipTone
  text: string
}

const plural = (value: number, one: string, many: string): string => (value === 1 ? one : many)

export const attendanceChips = (report: AccessReportResponse): AttendanceChip[] => {
  const chips: AttendanceChip[] = []
  const { alerts } = report
  if (alerts.sectorsAbove95 !== null && alerts.sectorsAbove95 > 0) {
    chips.push({
      key: 'above95',
      tone: 'good',
      text: `${alerts.sectorsAbove95} ${plural(alerts.sectorsAbove95, 'setor', 'setores')} acima de 95%`,
    })
  }
  if (alerts.sectorsWithoutReads !== null && alerts.sectorsWithoutReads > 0) {
    chips.push({
      key: 'noReads',
      tone: 'crit',
      text: `${alerts.sectorsWithoutReads} ${plural(alerts.sectorsWithoutReads, 'setor', 'setores')} sem leitura`,
    })
  }
  if (alerts.halfAt !== null) {
    chips.push({ key: 'half', tone: 'info', text: `Metade do público entrou até ${formatDayTime(alerts.halfAt)}` })
  }
  return chips
}

export type MetricSortKey =
  | 'label'
  | 'issued'
  | 'entries'
  | 'pending'
  | 'attendance'
  | 'revenue'
  | 'unused'
  | 'peak'
  | 'peakAt'
  | 'halfAt'
  | 'firstAt'
  | 'lastAt'

export interface MetricSort {
  key: string
  direction: 'asc' | 'desc'
}

const sortValue = (group: AccessReportGroup, key: string): number | string | null => {
  switch (key as MetricSortKey) {
    case 'label':
      return group.label
    case 'issued':
      return group.issued
    case 'entries':
      return group.entries
    case 'pending':
      return group.pending
    case 'attendance':
      return group.attendance
    case 'revenue':
      return group.revenueCents ?? null
    case 'unused':
      return group.unusedRevenueCents ?? null
    case 'peak':
      return group.peakMinute?.entries ?? null
    case 'peakAt':
      return group.peakMinute?.at ?? null
    case 'halfAt':
      return group.halfAt
    case 'firstAt':
      return group.firstAt
    case 'lastAt':
      return group.lastAt
    default:
      return null
  }
}

export const sortGroups = (groups: AccessReportGroup[], sort: MetricSort | null): AccessReportGroup[] => {
  if (sort === null) return groups
  const direction = sort.direction === 'asc' ? 1 : -1
  return groups.slice().sort((a, b) => {
    const left = sortValue(a, sort.key)
    const right = sortValue(b, sort.key)
    if (left === right) return 0
    if (left === null) return 1
    if (right === null) return -1
    if (typeof left === 'string' && typeof right === 'string') return left.localeCompare(right, 'pt-BR') * direction
    return (left < right ? -1 : 1) * direction
  })
}

export const nextSort = (current: MetricSort | null, key: string): MetricSort | null => {
  if (current === null || current.key !== key) return { key, direction: key === 'label' ? 'asc' : 'desc' }
  if (current.direction === (key === 'label' ? 'asc' : 'desc')) {
    return { key, direction: key === 'label' ? 'desc' : 'asc' }
  }
  return null
}

export type GroupPill = 'sem leitura' | 'sem emissão' | null

export const groupPill = (metrics: Pick<AccessMetrics, 'issued' | 'entries'>): GroupPill => {
  if (metrics.issued === null) return null
  if (metrics.issued === 0) return 'sem emissão'
  return metrics.entries === 0 ? 'sem leitura' : null
}

export const isLive = (report: AccessReportResponse | undefined): boolean => report?.event.status === 'live'

export interface DeviceGapRow {
  key: string
  device: string
  deviceId: string
  minutes: string
  window: string
}

export const deviceGapSummary = (count: number): string =>
  `${formatCount(count)} ${plural(count, 'leitor ficou', 'leitores ficaram')} mais de 20 min sem leitura enquanto outros seguiam lendo`

export const deviceGapRows = (report: AccessReportResponse): DeviceGapRow[] => {
  const gaps = report.alerts.deviceGaps
  const days = new Set(gaps.flatMap((gap) => [gap.from.slice(0, 10), gap.to.slice(0, 10)]))
  const moment = (local: string): string => (days.size > 1 ? formatDayTime(local) : formatClock(local))
  return gaps.map((gap, index) => ({
    key: `${gap.deviceId}-${index}`,
    device: deviceLabel(gap.deviceId),
    deviceId: gap.deviceId,
    minutes: `${formatCount(gap.minutes)} min`,
    window: `${moment(gap.from)}–${moment(gap.to)}`,
  }))
}
